<template>
  <div v-if="invoice">
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-4xl font-extrabold pl-2 tracking-tight text-gray-900 my-4">
        {{ invoiceTitle }} - {{ invoice.period }}
      </h1>
      <p>{{ invoice.entityName }}</p>
    </div>
    <div class="bg-white rounded-xl py-6 px-4 mt-4 mb-8 flex justify-between items-center">
      <h3>
        {{ invoiceAmountSummary }} :
        <span class="font-semibold">{{ formatPrice(invoice.amount) }}</span>
      </h3>
      <div class="flex items-center gap-4">
        <button type="button" class="flex cursor-pointer items-center" @click.stop="downloadInvoice(invoice)">
          <CloudDownloadIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
          <span class="pl-2 text-base font-semibold text-primary-dark">
            {{ invoice.reportFileUrl ? $t("invoice.download_invoice_pdf") : $t("Download") }}
          </span>
        </button>
        <button
          v-if="invoice.reportFileUrl"
          type="button"
          class="flex cursor-pointer items-center"
          @click.stop="downloadReportFile(invoice)"
        >
          <CloudDownloadIcon class="h-6 w-6 text-primary-dark" aria-hidden="true" />
          <span class="pl-2 text-base font-semibold text-primary-dark">
            {{ $t("invoice.download_report_csv") }}
          </span>
        </button>
      </div>
    </div>
    <div>
      <div class="mb-4">
        <div v-if="tabs.length > 1">
          <div class="border-b border-gray-200">
            <nav class="-mb-px flex space-x-8" aria-label="Tabs">
              <a
                v-for="tab in tabs"
                :key="tab.id"
                :href="generateTabLink(tab.id)"
                @click.prevent="selectTab(tab.id)"
                :class="[
                  tab.active
                    ? 'border-primary text-primary-dark'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium cursor-pointer',
                ]"
              >
                {{ tab.name }}
              </a>
            </nav>
          </div>
        </div>
      </div>

      <component :is="currentTab.component" :invoice="invoice" />
    </div>
  </div>
</template>

<script>
import InvoicesService from "@/api/services/invoices";
import { mapActions } from "vuex";
import { formatPrice, isPresent, downloadFromUrl } from "@estaly/ui";
import InvoiceBreakdown from "@/components/billing/invoice/InvoiceBreakdown.vue";
import InvoiceItems from "@/components/billing/invoice/InvoiceItems.vue";
import { CloudDownloadIcon } from "@heroicons/vue/outline";

export default {
  components: {
    CloudDownloadIcon,
    InvoiceBreakdown,
    InvoiceItems,
  },
  data() {
    return {
      invoice: null,
      selectedTabId: "invoice-breakdown",
    };
  },

  computed: {
    currentTab() {
      return this.tabs.find((tab) => tab.id === this.selectedTabId);
    },

    invoiceTitle() {
      if (!this.invoice) return "";
      if (this.invoice.transactionType === "statement") {
        return this.$t("invoice.statementTitle");
      } else if (this.invoice.transactionType === "bill") {
        return this.$t("invoice.billTitle");
      }
      return "";
    },

    invoiceAmountSummary() {
      if (!this.invoice) return "";
      if (this.invoice.transactionType === "statement") {
        return this.$t("invoice.statementAmount");
      } else if (this.invoice.transactionType === "bill") {
        return this.$t("invoice.billAmount");
      }
      return "";
    },

    tabs() {
      return [
        {
          id: "invoice-breakdown",
          name: "Répartition des ventes",
          active: true,
          component: InvoiceBreakdown,
        },
        {
          id: "invoice-items",
          name: "Détails des encaissements",
          active: false,
          component: InvoiceItems,
        },
      ];
    },
  },

  beforeMount() {
    this.syncTabWithUrl();
    this.getInvoice();
  },

  watch: {
    $route: "syncTabWithUrl",
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    formatPrice,
    isPresent,

    selectTab(selectedTabId, replaceUrl = true) {
      this.selectedTabId = selectedTabId;
      this.tabs.forEach((tab) => {
        tab.active = tab.id === selectedTabId;
      });

      if (replaceUrl) {
        this.$router.push({ query: { tab: selectedTabId } });
      } else {
        this.$router.replace({ query: { tab: selectedTabId } });
      }
    },

    syncTabWithUrl() {
      const tabId = this.$route.query.tab || "invoice-breakdown";
      if (tabId !== this.currentTab.id) {
        this.selectTab(tabId, false);
      }
    },

    generateTabLink(tabId) {
      return this.$router.resolve({ query: { tab: tabId } }).href;
    },

    async getInvoice() {
      const response = await InvoicesService.fetch(this.$route.params.id);
      if (response?.success) {
        this.invoice = response.data.invoice;
      }
    },

    downloadInvoice(invoice) {
      downloadFromUrl(invoice.fileUrl, `${invoice.friendlyId}.pdf`);
    },

    downloadReportFile(invoice) {
      downloadFromUrl(invoice.reportFileUrl, `${invoice.friendlyId}_bordereau.csv`);
    },
  },
};
</script>
